import React, { useState } from "react";
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Label,
  Checkbox,
} from "flowbite-react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "../../../toast";
import Select from "react-select";
import { useUpdateAssignmentMutation } from "../../../graphql/generated.tsx";

const languageOptions = [
  { value: "C", label: "C" },
  { value: "Python", label: "Python" },
  { value: "Java", label: "Java" },
  { value: "Cpp", label: "Cpp" },
];

const EditAssignmentModal = ({ id, setAssignments, onClose, assignment }) => {
  const assignmentDuration = Number(assignment.duration) || 0;
  const baseHours = Math.floor(assignmentDuration);
  const baseMinutes = Math.round((assignmentDuration - baseHours) * 60);
  const normalizedHours = baseMinutes === 60 ? baseHours + 1 : baseHours;
  const normalizedMinutes = baseMinutes === 60 ? 0 : baseMinutes;

  const [name, setName] = useState(assignment.name || "");
  const [shortDescription, setShortDescription] = useState(
    assignment.short_description || ""
  );
  const [enableProctoring, setEnableProctoring] = useState(
    assignment.enable_proctoring || false
  );
  const [maxTabSwitches, setMaxTabSwitches] = useState(
    assignment.max_tabSwitches || 0
  );
  const [durationHours, setDurationHours] = useState(normalizedHours);
  const [durationMinutes, setDurationMinutes] = useState(normalizedMinutes);
  const [description, setDescription] = useState(assignment.description || "");
  const [dueDate, setDueDate] = useState(
    assignment.due_date || assignment.end_time
      ? new Date(assignment.due_date || assignment.end_time)
      : null
  );
  const [startTime, setStartTime] = useState(
    assignment.start_time ? new Date(assignment.start_time) : null
  );
  const [languages, setLanguages] = useState(
    assignment.languages.map((lang) => ({ value: lang, label: lang })) || []
  );
  const [domain, setDomain] = useState(assignment.domain || "");
  const [maxCheatCount, setMaxCheatCount] = useState(
    assignment.max_cheat_count || 0
  );

  const [updateAssignment] = useUpdateAssignmentMutation({
    onCompleted: (data) => {
      setAssignments(data.updateAssignment);
      toast.success("Assignment updated successfully");
      onClose();
    },
    onError: (error) => {
      console.error("Error updating assignment:", error);
      toast.error("Error updating assignment");
    },
  });

  const handleUpdate = () => {
    if (!enableProctoring) {
      setMaxCheatCount(0);
      setMaxTabSwitches(0);
    }

    const parsedStart = startTime instanceof Date ? startTime : null;
    const parsedDue = dueDate instanceof Date ? dueDate : null;

    if (parsedStart && Number.isNaN(parsedStart.getTime())) {
      toast.error("Please select a valid start date & time");
      return;
    }
    if (!parsedDue || Number.isNaN(parsedDue.getTime())) {
      toast.error("Please select a valid due date & time");
      return;
    }
    if (parsedStart && parsedStart.getTime() >= parsedDue.getTime()) {
      toast.error("Due date must be after start time");
      return;
    }

    const parsedDurationHours = Number(durationHours) || 0;
    const parsedDurationMinutes = Number(durationMinutes) || 0;

    if (
      parsedDurationHours < 0 ||
      parsedDurationMinutes < 0 ||
      parsedDurationMinutes > 59
    ) {
      toast.error("Please enter a valid duration (minutes should be 0 to 59)");
      return;
    }

    if (parsedDurationHours === 0 && parsedDurationMinutes === 0) {
      toast.error("Duration should be greater than 0");
      return;
    }

    const totalDurationInHours =
      parsedDurationHours + parsedDurationMinutes / 60;

    updateAssignment({
      variables: {
        id,
        updateAssignmentInput: {
          id,
          name,
          short_description: shortDescription,
          description,
          ...(parsedStart ? { start_time: parsedStart } : {}),
          due_date: parsedDue,
          languages: languages.map((lang) => lang.value),
          domain,
          enable_proctoring: enableProctoring,
          max_tabSwitches: parseInt(maxTabSwitches),
          max_cheat_count: parseInt(maxCheatCount),
          duration: totalDurationInHours,
        },
      },
    });
  };

  return (
    <Modal show={true} onClose={onClose}>
      <Modal.Header>Edit Assignment</Modal.Header>
      <Modal.Body>
        <div className="block">
          <Label htmlFor="name" value="Name :" className="font-semibold" />
          <TextInput
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="block mt-4">
          <Label
            htmlFor="shortDescription"
            value="Short Description :"
            className="font-semibold"
          />
          <TextInput
            id="shortDescription"
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
          />
        </div>
        <div className="block mt-4">
          <Label
            htmlFor="description"
            value="Description :"
            className="font-semibold"
          />
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
          <div className="block">
            <Label
              htmlFor="startTime"
              value="Start Date & Time :"
              className="font-semibold"
            />
            <ReactDatePicker
              selected={startTime}
              onChange={(date) => {
                setStartTime(date);
                if (date && dueDate && dueDate.getTime() < date.getTime()) {
                  setDueDate(null);
                }
              }}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="MMM d, yyyy h:mm aa"
              placeholderText="Select start"
              className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
              calendarClassName="bt-calendar"
            />
          </div>
          <div className="block">
            <Label
              htmlFor="dueDate"
              value="Due Date & Time :"
              className="font-semibold"
            />
            <ReactDatePicker
              selected={dueDate}
              onChange={(date) => setDueDate(date)}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="MMM d, yyyy h:mm aa"
              placeholderText="Select due date"
              minDate={startTime || undefined}
              className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
              calendarClassName="bt-calendar"
            />
          </div>
        </div>
        <div className="block mt-4">
          <Label
            htmlFor="duration"
            value="Duration :"
            className="font-semibold"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="durationHours" value="Hours" className="font-semibold" />
              <TextInput
                type="number"
                id="durationHours"
                min={0}
                value={durationHours}
                onChange={(e) => setDurationHours(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="durationMinutes" value="Minutes" className="font-semibold" />
              <TextInput
                type="number"
                id="durationMinutes"
                min={0}
                max={59}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
              />
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">Example: 1 hour 15 minutes = 1h 15m.</p>
        </div>
        <div className="my-5">
          <Checkbox
            id="enableProctoring"
            checked={enableProctoring}
            onChange={(e) => setEnableProctoring(e.target.checked)}
          />
          <Label
            htmlFor="enableProctoring"
            value="Enable Proctoring"
            className="ml-2"
          />
        </div>

        {enableProctoring && (
          <>
            <div className="block mt-4">
              <Label
                htmlFor="maxCheatCount"
                value="Maximum Cheat Count :"
                className="font-semibold"
              />
              <TextInput
                type="number"
                id="maxCheatCount"
                value={maxCheatCount}
                onChange={(e) => setMaxCheatCount(e.target.value)}
              />
            </div>
            <div className="block mt-4">
              <Label
                htmlFor="maxTabSwitches"
                value="Maximum Tab Switches :"
                className="font-semibold"
              />
              <TextInput
                type="number"
                id="maxTabSwitches"
                value={maxTabSwitches}
                onChange={(e) => setMaxTabSwitches(e.target.value)}
              />
            </div>
          </>
        )}
        <div className="block mt-4">
          <Label
            htmlFor="languages"
            value="Languages :"
            className="font-semibold"
          />
          <Select
            isMulti
            options={languageOptions}
            value={languages}
            onChange={(selectedOptions) => setLanguages(selectedOptions)}
          />
        </div>
        <div className="block mt-4">
          <Label htmlFor="domain" value="Domain :" className="font-semibold" />
          <TextInput
            id="domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleUpdate} color="success" className="p-0">
          Update
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAssignmentModal;
